import { Card, CardContent, Input } from '../../../components/ui';

interface SocialFooterSectionProps {
    // Facebook
    facebookUsername?: string;
    onFacebookUsernameChange: (value: string) => void;
    facebookFollowers?: number;
    onFacebookFollowersChange: (value: number) => void;
    // Instagram
    instagramUsername?: string;
    onInstagramUsernameChange: (value: string) => void;
    instagramFollowers?: number;
    onInstagramFollowersChange: (value: number) => void;
    // Footer
    licenseNumber?: string;
    onLicenseNumberChange: (value: string) => void;
    minimumLegalDrinkingAge?: number;
    onMinimumLegalDrinkingAgeChange: (value: number) => void;
}

export default function SocialFooterSection({
    facebookUsername,
    onFacebookUsernameChange,
    facebookFollowers,
    onFacebookFollowersChange,
    instagramUsername,
    onInstagramUsernameChange,
    instagramFollowers,
    onInstagramFollowersChange,
    licenseNumber,
    onLicenseNumberChange,
    minimumLegalDrinkingAge,
    onMinimumLegalDrinkingAgeChange,
}: SocialFooterSectionProps) {
    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-[#e2e8f0]">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#06b6d4] to-[#22d3ee] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">7</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1e293b]">
                            Social Media & Footer
                        </h3>
                        <p className="text-sm text-[#64748b]">
                            Add social media information and footer details
                        </p>
                    </div>
                </div>

                {/* Social Media Section */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 bg-[#ecfeff] p-3 rounded-lg border border-[#a5f3fc]">
                        <svg
                            className="w-5 h-5 text-[#06b6d4]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                            />
                        </svg>
                        <h4 className="text-md font-semibold text-[#1e293b]">Social Media</h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Facebook */}
                        <div className="p-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg space-y-3">
                            <h5 className="font-medium text-[#1e293b] flex items-center gap-2">
                                <svg
                                    className="w-5 h-5 text-[#1877f2]"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                                Facebook
                            </h5>
                            <Input
                                label="Username"
                                type="text"
                                value={facebookUsername || ''}
                                onChange={(e) => onFacebookUsernameChange(e.target.value)}
                                placeholder="mybrand"
                                maxLength={50}
                            />
                            <Input
                                label="Followers (Optional)"
                                type="number"
                                value={facebookFollowers || ''}
                                onChange={(e) => onFacebookFollowersChange(Number(e.target.value))}
                                placeholder="15000"
                                min={0}
                            />
                        </div>

                        {/* Instagram */}
                        <div className="p-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg space-y-3">
                            <h5 className="font-medium text-[#1e293b] flex items-center gap-2">
                                <svg
                                    className="w-5 h-5 text-[#E4405F]"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                </svg>
                                Instagram
                            </h5>
                            <Input
                                label="Username"
                                type="text"
                                value={instagramUsername || ''}
                                onChange={(e) => onInstagramUsernameChange(e.target.value)}
                                placeholder="mybrand_official"
                                maxLength={50}
                            />
                            <Input
                                label="Followers (Optional)"
                                type="number"
                                value={instagramFollowers || ''}
                                onChange={(e) => onInstagramFollowersChange(Number(e.target.value))}
                                placeholder="25000"
                                min={0}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer Section */}
                <div className="pt-4 border-t border-[#e2e8f0] space-y-4">
                    <h4 className="text-md font-semibold text-[#1e293b]">Footer Content</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* License Number */}
                        <Input
                            label="License Number"
                            type="text"
                            value={licenseNumber || ''}
                            onChange={(e) => onLicenseNumberChange(e.target.value)}
                            placeholder="L/01/32432435"
                            required
                        />

                        {/* Minimum Legal Drinking Age */}
                        <Input
                            label="Minimum Legal Drinking Age"
                            type="number"
                            value={minimumLegalDrinkingAge || ''}
                            onChange={(e) =>
                                onMinimumLegalDrinkingAgeChange(Number(e.target.value))
                            }
                            placeholder="21"
                            required
                            min={0}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
